/**
 * Answering a phone that asked for easy sign-in.
 *
 * The code comes first and largest, because it is the whole of how the administrator knows
 * which device this is: the one in front of them shows it, or the driver reads it out over the
 * telephone. Then whose phone it becomes — a driver on the books, or a new one with no address
 * or password — and the car, chosen or registered here. It becomes that driver's own phone:
 * the firm's devices are registered from its devices page alone. One Approve does all of it;
 * the server checks the request first, so an answer that came too late creates nobody.
 */

import { useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { describe } from '../settings/errors';
import { fleetApi, type FleetCategory, type FleetDriver, type FleetVehicle } from '../settings/fleet/api';
import { useTenantFormats } from '../shell/formats';
import { Banner, Button, Dialog, Field, Input, Segmented, Select } from '../ui';
import { devicesApi, type ApproveDevice, type DeviceRequest } from './api';

type DriverChoice = 'existing' | 'new';
type CarChoice = 'none' | 'existing' | 'new';

const blankCar = { plate: '', make: '', model: '', categoryCode: '', seats: '4' };

export function ApproveDeviceDialog({
  request,
  onDone,
  onClose,
}: {
  request: DeviceRequest;
  onDone: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const form = useId();

  const [drivers, setDrivers] = useState<FleetDriver[]>([]);
  const [vehicles, setVehicles] = useState<FleetVehicle[]>([]);
  const [categories, setCategories] = useState<FleetCategory[]>([]);

  const [driverChoice, setDriverChoice] = useState<DriverChoice>('existing');
  const [driverId, setDriverId] = useState('');
  const [person, setPerson] = useState({ displayName: '', phoneNumber: '', callSign: '' });

  const [carChoice, setCarChoice] = useState<CarChoice>('existing');
  const [vehicleId, setVehicleId] = useState('');
  const [car, setCar] = useState(blankCar);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const [roster, fleet, classes] = await Promise.all([
          fleetApi.drivers(),
          fleetApi.vehicles(),
          fleetApi.categories(),
        ]);

        if (cancelled) return;

        const active = roster.filter((one) => one.isActive);

        setDrivers(active);
        setVehicles(fleet.filter((one) => one.isActive));
        setCategories(classes);

        // A firm with nobody on the books yet is here to create the first one, and one with
        // no car yet to register one.
        if (active.length === 0) setDriverChoice('new');
        if (!fleet.some((one) => one.isActive)) setCarChoice('new');
      } catch (failure) {
        if (!cancelled) setError(describe(failure, t, 'devices.couldNotLoad'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const blank = (value: string) => (value.trim() === '' ? null : value.trim());

  async function approve(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const body: ApproveDevice = {
      driverId: driverChoice === 'existing' ? driverId : null,
      newDriver:
        driverChoice === 'new'
          ? {
              displayName: person.displayName.trim(),
              phoneNumber: blank(person.phoneNumber),
              callSign: blank(person.callSign),
              notes: null,
            }
          : null,
      vehicleId: carChoice === 'existing' && vehicleId !== '' ? vehicleId : null,
      newVehicle:
        carChoice === 'new'
          ? {
              plate: car.plate.trim(),
              make: car.make.trim(),
              model: car.model.trim(),
              colour: null,
              year: null,
              categoryCode: car.categoryCode === '' ? (categories[0]?.code ?? '') : car.categoryCode,
              seats: Number(car.seats),
              notes: null,
              featureCodes: null,
            }
          : null,
    };

    try {
      await devicesApi.approve(request.id, body);
      onDone();
    } catch (failure) {
      setError(describe(failure, t, 'devices.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    setBusy(true);
    setError(null);

    try {
      await devicesApi.reject(request.id);
      onDone();
    } catch (failure) {
      setError(describe(failure, t, 'devices.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      wide
      title={t('devices.approve.title')}
      onClose={onClose}
      actions={
        <>
          <Button variant="quiet" onClick={onClose}>
            {t('devices.close')}
          </Button>
          <Button disabled={busy} onClick={() => void reject()}>
            {t('devices.approve.reject')}
          </Button>
          <Button variant="primary" type="submit" form={form} disabled={busy}>
            {t('devices.approve.approve')}
          </Button>
        </>
      }
    >
      <form id={form} onSubmit={(event) => void approve(event)} className="flex flex-col gap-4">
        {error !== null && <Banner tone="danger">{error}</Banner>}

        <div className="flex items-baseline gap-4">
          <span className="font-mono text-[2.5rem] leading-none tracking-[0.2em]">{request.code}</span>
          <span className="text-secondary text-ink-3">
            {request.deviceName ?? t('devices.unnamed')}
            <span className="block">{t('devices.approve.asked', { time: formats.time(request.createdAt) })}</span>
          </span>
        </div>
        <p className="m-0 text-secondary text-ink-3">{t('devices.approve.matchCode')}</p>
        <p className="m-0 text-secondary text-ink-3">{t('devices.approve.personalHelp')}</p>

        <Segmented
          label={t('devices.approve.whose')}
          value={driverChoice}
          options={[
            { value: 'existing', label: t('devices.approve.existingDriver') },
            { value: 'new', label: t('devices.approve.newDriver') },
          ]}
          onChange={setDriverChoice}
        />

        {driverChoice === 'existing' ? (
          <Field id="driverId" label={t('devices.approve.driver')}>
            <Select value={driverId} onChange={(e) => setDriverId(e.target.value)} required>
              <option value="">{t('devices.approve.pickDriver')}</option>
              {drivers.map((one) => (
                <option key={one.id} value={one.id}>
                  {one.callSign === null ? one.displayName : `${one.callSign} · ${one.displayName}`}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <Field id="displayName" label={t('fleet.drivers.name')}>
              <Input
                value={person.displayName}
                onChange={(e) => setPerson({ ...person, displayName: e.target.value })}
                required
              />
            </Field>
            <Field id="callSign" label={t('fleet.drivers.callSign')}>
              <Input value={person.callSign} onChange={(e) => setPerson({ ...person, callSign: e.target.value })} />
            </Field>
            <Field id="phoneNumber" label={t('fleet.drivers.phone')}>
              <Input
                value={person.phoneNumber}
                onChange={(e) => setPerson({ ...person, phoneNumber: e.target.value })}
              />
            </Field>
          </div>
        )}

        <Segmented
          label={t('devices.approve.whichCar')}
          value={carChoice}
          options={[
            { value: 'existing', label: t('devices.approve.existingCar') },
            { value: 'new', label: t('devices.approve.newCar') },
            { value: 'none', label: t('devices.approve.noCar') },
          ]}
          onChange={setCarChoice}
        />

        {carChoice === 'existing' && (
          <Field id="vehicleId" label={t('devices.approve.car')} help={t('devices.approve.carHelp')}>
            <Select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} required>
              <option value="">{t('devices.approve.pickCar')}</option>
              {vehicles.map((one) => (
                <option key={one.id} value={one.id}>
                  {`${one.plate} · ${one.make} ${one.model}`}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {carChoice === 'new' && (
          <div className="grid grid-cols-5 gap-3">
            <Field id="plate" label={t('fleet.vehicles.plate')}>
              <Input value={car.plate} onChange={(e) => setCar({ ...car, plate: e.target.value })} required />
            </Field>
            <Field id="make" label={t('fleet.vehicles.make')}>
              <Input value={car.make} onChange={(e) => setCar({ ...car, make: e.target.value })} required />
            </Field>
            <Field id="model" label={t('fleet.vehicles.model')}>
              <Input value={car.model} onChange={(e) => setCar({ ...car, model: e.target.value })} required />
            </Field>
            <Field id="categoryCode" label={t('fleet.vehicles.category')}>
              <Select value={car.categoryCode} onChange={(e) => setCar({ ...car, categoryCode: e.target.value })}>
                {categories.map((one) => (
                  <option key={one.code} value={one.code}>
                    {one.displayName}
                  </option>
                ))}
              </Select>
            </Field>
            <Field id="seats" label={t('fleet.vehicles.seatsLabel')}>
              <Input
                type="number"
                min={1}
                max={9}
                value={car.seats}
                onChange={(e) => setCar({ ...car, seats: e.target.value })}
                required
              />
            </Field>
          </div>
        )}
      </form>
    </Dialog>
  );
}
